import request from 'graphql-request'
import { CustomerTotals, TimeSeriesTotals } from 'types/api'

const {
  VITE_GQL_ENDPOINT: appsyncUrl
} = import.meta.env

const gqlUrl = appsyncUrl as string + '/graphql'

const regionNames = new Intl.DisplayNames(['en'], { type: 'region' })

// Though Shopify mainly follows the ISO 3166-1 alpha-2 - it has some exceptions
const shopifyCodeExceptions = {
  XK: 'Kosovo',
  AC: 'Ascension Island',
  TA: 'Tristan da Cunha',
  ZZ: 'Unknown Region',
  US: 'United States' // prefer this name to Unites States of America
}

export const getCountryNameFromCode = (code: string): string | undefined => {
  if (code in shopifyCodeExceptions) {
    return shopifyCodeExceptions[code]
  }
  return regionNames.of(code)
}

export interface DaySales {
  day: number
  name: string
  hours: Record<string, { label: string, ratio: number }>
};

const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

export const formatPrice = (x: number): string => {
  const numberString = new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(x)
  return `$${numberString}`
}

export const shortDateEpochFormatter = (e): string => shortDateFormatter(new Date(e))

export const shortDateFormatter = (dt: Date): string => {
  return new Intl.DateTimeFormat(undefined, { month: 'short', day: 'numeric' }).format(dt)
}

export const moneyFormatter = (value: number): string =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD'
  }).format(value)

export const numberFormatter = (value: number): string => value.toLocaleString()

export const createTooltipFormatter = (
  dataKey: string,
  secondaryDataKey: string,
  formatter: (v: number) => string,
  secondaryFormatter: (v: number) => string
): ((value: number, name: string) => string | undefined) =>
  (value, name) => {
    const lowerName = name.toLowerCase()
    if (lowerName === dataKey.toLowerCase()) return formatter(value)
    if (lowerName === secondaryDataKey.toLowerCase()) return secondaryFormatter(value)
    return undefined
  }

export const cleanVariables = (input: Record<string, any>): Record<string, any> =>
  Object.fromEntries(
    Object.entries(input)
      .map(([key, value]) => {
        if (Array.isArray(value)) {
          const filtered = value.filter(v => v !== 'all')
          return [key, filtered]
        }
        return [key, value]
      })
      .filter(([_, value]) => value !== undefined && value !== 'all')
  )
export const graphqlRequest = async <T>(
  query: string,
  variables: Record<string, any>,
  auth0Token: string
): Promise<T> => {
  return await request<T>(gqlUrl, query, variables, {
    Authorization: auth0Token
  })
}

const hourSlotLabels = [
  '12am-4am',
  '4am-8am',
  '8am-12pm',
  '12pm-4pm',
  '4pm-8pm',
  '8pm-12am'
]

export const transformSalesDataToLocalTime = (
  data: TimeSeriesTotals[]
): DaySales[] => {
  const HOURS_PER_SLOT = 4
  const SLOTS_PER_DAY = 24 / HOURS_PER_SLOT

  const grouped = data.reduce<Record<number, number[]>>((acc, { timestamp, sales }) => {
    if (sales == null) return acc

    const date = new Date(timestamp)
    const day = date.getDay()
    const hour = date.getHours()
    const slot = Math.floor(hour / HOURS_PER_SLOT)

    const daySlots = acc[day] ?? Array(SLOTS_PER_DAY).fill(0)
    daySlots[slot] += sales

    acc[day] = daySlots
    return acc
  }, {})

  const maxSale = Math.max(0, ...Object.values(grouped).flat())

  return Array.from({ length: 7 }, (_, i): DaySales => {
    const rawSlots = grouped[i] ?? Array(SLOTS_PER_DAY).fill(0)

    const namedSlots: Record<string, { label: string, ratio: number }> = {}

    rawSlots.forEach((sale, idx) => {
      const label = hourSlotLabels[idx]
      namedSlots[label] = {
        label: `$${sale.toFixed(2)}`,
        ratio: maxSale > 0 ? sale / maxSale : 0
      }
    })

    return {
      day: i + 1,
      name: dayNames[i],
      hours: namedSlots
    }
  })
}

export const getRatioClass = (ratio: number): string => {
  if (ratio >= 0.95) return 'bg-brand'
  if (ratio >= 0.8) return 'bg-brand/90'
  if (ratio >= 0.6) return 'bg-brand/70'
  if (ratio >= 0.4) return 'bg-brand/60'
  if (ratio >= 0.25) return 'bg-brand/50'
  if (ratio >= 0.1) return 'bg-brand/30'
  if (ratio >= 0.01) return 'bg-brand/20'
  return 'bg-lm-bg-secondary dark:bg-dm-bg-secondary'
}

export const addCustomerName = (rows: CustomerTotals[], clientTitle: string): CustomerTotals[] => {
  return rows.map((r, index) => ({
    ...r,
    customerName: (() => {
      const name = [r.customerFirst, r.customerLast]
        .filter(v => v != null && String(v).trim() !== '')
        .join(' ')
      return name !== '' ? name : `${clientTitle} Fan ${index + 1} `
    })()
  }))
}
